/**
 * Shared SEM event routing for stateless chat hooks
 *
 * This module contains the event handling logic that routes SEM events
 * through the Redux timeline infrastructure. It's shared between
 * useStatelessChat and useStatelessChatStream to avoid duplication.
 */

import { handleSem } from '@platform/sem/registry';
import { toolLabelProviderFor } from '@platform/sem/toolLabels';
import { appendRunDelta, completeRun, persistRun, startRun } from '@platform/summary-ui';
import type {
  ArtifactMetaEntity,
  MessageEntity,
  StatusEntity,
  TeamAnalysisEntity,
  ToolCallEntity,
  ToolResultEntity,
} from '@platform/timeline';
import {
  addEntities,
  addEntity,
  appendEntityArrayItem,
  appendMessageText,
  finalizeMessage,
  removeEntity,
  updateEntity,
  updateToolProgress,
  upsertEntity,
} from '@platform/timeline';
import type { Dispatch } from '@reduxjs/toolkit';

/** SEM event structure */
export interface SemEvent {
  type: string;
  id: string;
  role?: string;
  delta?: string;
  cumulative?: string;
  text?: string;
  metadata?: Record<string, unknown>;
  message?: string;
  name?: string;
  input?: unknown;
  patch?: Record<string, unknown>;
  result?: unknown;
  title?: string;
  data?: Record<string, unknown>;
}

/**
 * Extract stable ID from item ID by removing the `:N` suffix
 */
export function stableIdFromItemId(rawId: string): string {
  const colonIndex = rawId.lastIndexOf(':');
  if (colonIndex > 0) {
    return rawId.substring(0, colonIndex);
  }
  return rawId;
}

const ARTIFACT_OPEN_TAG = '<mento:artifact:v1>';
const ARTIFACT_CLOSE_TAG = '</mento:artifact:v1>';
// Shortened closing tags Claude Sonnet 5.5 sometimes writes instead (MEN-5536). Keep in
// step with closingTags in backend/pkg/artifact/helpers/chunk_parser.go so the live view
// matches the saved artifact.
const ARTIFACT_SHORTENED_CLOSE_TAGS = ['</artifact:v1>', '</artifact>'];

type ParsedArtifactTaggedText = {
  outsideText: string;
  artifactText: string;
  insideArtifact: boolean;
  hasArtifactTag: boolean;
  // Number of opening tags seen. A later block restarts the artifact, as the backend
  // saves only the final attempt.
  attempts: number;
};

type LlmArtifactProjectionState = {
  outsideLength: number;
  artifactLength: number;
  insideArtifact: boolean;
  attempts: number;
  summaryStarted: boolean;
  lastCumulative: string;
  summaryStableId: string;
  summaryEntityId: string;
};

const llmArtifactProjectionByMessage = new Map<string, LlmArtifactProjectionState>();

function artifactProjectionKey(convId: string, messageID: string): string {
  return `${convId}:${messageID}`;
}

function trailingTagPrefixLen(text: string, tag: string): number {
  const maxLen = Math.min(text.length, tag.length - 1);
  for (let len = maxLen; len > 0; len -= 1) {
    if (text.endsWith(tag.slice(0, len))) {
      return len;
    }
  }
  return 0;
}

function stripTrailingArtifactTagPrefix(text: string, insideArtifact: boolean): string {
  if (!text) return text;

  const openPrefixLen = insideArtifact ? 0 : trailingTagPrefixLen(text, ARTIFACT_OPEN_TAG);
  const closePrefixLen = Math.max(
    ...[ARTIFACT_CLOSE_TAG, ...ARTIFACT_SHORTENED_CLOSE_TAGS].map((tag) =>
      trailingTagPrefixLen(text, tag)
    )
  );
  const stripLen = Math.max(openPrefixLen, closePrefixLen);
  if (stripLen <= 0) {
    return text;
  }
  return text.slice(0, text.length - stripLen);
}

// stripShortenedCloseTags removes stray shortened closing tags left inside a block that
// the exact tag closed, as backend normalization does before saving.
function stripShortenedCloseTags(text: string): string {
  return ARTIFACT_SHORTENED_CLOSE_TAGS.reduce((out, tag) => out.split(tag).join(''), text);
}

// findArtifactClose mirrors the backend rule: the exact closing tag wins when it appears
// before the next opening tag; otherwise the earliest shortened closing tag before the
// next opening tag ends the block.
function findArtifactClose(text: string, cursor: number): { index: number; length: number } | null {
  const exactIdx = text.indexOf(ARTIFACT_CLOSE_TAG, cursor);
  const nextOpen = text.indexOf(ARTIFACT_OPEN_TAG, cursor);
  if (exactIdx !== -1 && (nextOpen === -1 || exactIdx < nextOpen)) {
    return { index: exactIdx, length: ARTIFACT_CLOSE_TAG.length };
  }
  const end = nextOpen === -1 ? text.length : nextOpen;
  let best: { index: number; length: number } | null = null;
  for (const tag of ARTIFACT_SHORTENED_CLOSE_TAGS) {
    const idx = text.indexOf(tag, cursor);
    if (idx !== -1 && idx < end && (best === null || idx < best.index)) {
      best = { index: idx, length: tag.length };
    }
  }
  if (best) return best;
  return exactIdx === -1 ? null : { index: exactIdx, length: ARTIFACT_CLOSE_TAG.length };
}

function parseArtifactTaggedText(
  text: string,
  options?: { holdTrailingTagPrefixes?: boolean }
): ParsedArtifactTaggedText {
  let outsideText = '';
  let artifactText = '';
  let cursor = 0;
  let insideArtifact = false;
  let hasArtifactTag = false;
  let attempts = 0;
  const holdTrailingTagPrefixes = options?.holdTrailingTagPrefixes === true;

  while (cursor < text.length) {
    if (!insideArtifact) {
      const openIdx = text.indexOf(ARTIFACT_OPEN_TAG, cursor);
      if (openIdx === -1) {
        const remainder = text.slice(cursor);
        outsideText += holdTrailingTagPrefixes
          ? stripTrailingArtifactTagPrefix(remainder, false)
          : remainder;
        break;
      }
      hasArtifactTag = true;
      outsideText += text.slice(cursor, openIdx);
      cursor = openIdx + ARTIFACT_OPEN_TAG.length;
      insideArtifact = true;
      attempts += 1;
      artifactText = '';
      continue;
    }

    const close = findArtifactClose(text, cursor);
    if (close === null) {
      const remainder = stripShortenedCloseTags(text.slice(cursor));
      artifactText += holdTrailingTagPrefixes
        ? stripTrailingArtifactTagPrefix(remainder, true)
        : remainder;
      return {
        outsideText,
        artifactText,
        insideArtifact: true,
        hasArtifactTag: hasArtifactTag || artifactText.length > 0,
        attempts,
      };
    }

    artifactText += stripShortenedCloseTags(text.slice(cursor, close.index));
    cursor = close.index + close.length;
    insideArtifact = false;
  }

  return {
    outsideText,
    artifactText,
    insideArtifact,
    hasArtifactTag: hasArtifactTag || artifactText.length > 0,
    attempts,
  };
}

function getOrCreateLlmArtifactProjectionState(
  convId: string,
  messageID: string
): LlmArtifactProjectionState {
  const key = artifactProjectionKey(convId, messageID);
  const existing = llmArtifactProjectionByMessage.get(key);
  if (existing) {
    return existing;
  }

  const summaryStableId = `llm-artifact:${messageID}`;
  const created: LlmArtifactProjectionState = {
    outsideLength: 0,
    artifactLength: 0,
    insideArtifact: false,
    attempts: 0,
    summaryStarted: false,
    lastCumulative: '',
    summaryStableId,
    summaryEntityId: `editable_summary:${summaryStableId}`,
  };
  llmArtifactProjectionByMessage.set(key, created);
  return created;
}

function clearLlmArtifactProjectionState(convId: string, messageID: string): void {
  llmArtifactProjectionByMessage.delete(artifactProjectionKey(convId, messageID));
}

function projectLlmArtifactTaggedText(params: {
  dispatch: Dispatch;
  convId: string;
  messageID: string;
  now: number;
  fullText: string;
  shouldFinalizeMessage: boolean;
}): void {
  const { dispatch, convId, messageID, now, fullText, shouldFinalizeMessage } = params;
  const state = getOrCreateLlmArtifactProjectionState(convId, messageID);
  const parsed = parseArtifactTaggedText(fullText, {
    holdTrailingTagPrefixes: !shouldFinalizeMessage,
  });

  if (parsed.hasArtifactTag) {
    if (!state.summaryStarted) {
      state.summaryStarted = true;
      dispatch(startRun({ convId, stableId: state.summaryStableId, now }));
      dispatch(
        addEntity({
          convId,
          entity: {
            id: state.summaryEntityId,
            kind: 'editable_summary',
            timestamp: now,
            props: { status: 'active', content: '', chunks: [] },
          } as any,
        })
      );
    }

    if (state.attempts > 0 && parsed.attempts > state.attempts) {
      // The model restarted the artifact; show only the new attempt.
      dispatch(startRun({ convId, stableId: state.summaryStableId, now }));
      dispatch(
        updateEntity({
          convId,
          entityId: state.summaryEntityId,
          props: { status: 'active', success: undefined, content: '', chunks: [] },
        })
      );
      state.artifactLength = 0;
      state.insideArtifact = true;
    }

    if (parsed.artifactText.length > state.artifactLength) {
      const delta = parsed.artifactText.slice(state.artifactLength);
      if (delta) {
        dispatch(appendRunDelta({ convId, stableId: state.summaryStableId, delta, now }));
        dispatch(
          appendEntityArrayItem({
            convId,
            entityId: state.summaryEntityId,
            key: 'chunks',
            item: delta,
          })
        );
      }
    }
  }

  if (parsed.outsideText.length > state.outsideLength) {
    const outsideDelta = parsed.outsideText.slice(state.outsideLength);
    if (outsideDelta) {
      dispatch(
        appendMessageText({
          convId,
          messageId: messageID,
          text: outsideDelta,
        })
      );
    }
  }

  if (state.summaryStarted && state.insideArtifact && !parsed.insideArtifact) {
    dispatch(
      updateEntity({
        convId,
        entityId: state.summaryEntityId,
        props: { status: 'completed', success: true },
      })
    );
    dispatch(completeRun({ convId, stableId: state.summaryStableId, success: true, now }));
  }

  state.outsideLength = parsed.outsideText.length;
  state.artifactLength = parsed.artifactText.length;
  state.insideArtifact = parsed.insideArtifact;
  state.attempts = parsed.attempts;
  state.lastCumulative = fullText;

  if (shouldFinalizeMessage) {
    dispatch(
      finalizeMessage({
        convId,
        messageId: messageID,
        fullText: parsed.outsideText,
      })
    );

    if (state.summaryStarted && parsed.insideArtifact) {
      dispatch(
        updateEntity({
          convId,
          entityId: state.summaryEntityId,
          props: {
            status: 'error',
            success: false,
            error: 'Artifact stream ended before closing tag',
          },
        })
      );
      dispatch(
        completeRun({
          convId,
          stableId: state.summaryStableId,
          success: false,
          error: 'Artifact stream ended before closing tag',
          now,
        })
      );
    }

    if (parsed.outsideText.trim() === '') {
      dispatch(removeEntity({ convId, entityId: messageID }));
    }
  }
}

/**
 * Dispatch summary UI events for artifact tracking
 */
export function dispatchSummaryUiSemEvent(params: {
  dispatch: Dispatch;
  convId: string;
  ev: SemEvent;
  now: number;
}): void {
  const { dispatch, convId, ev, now } = params;

  switch (ev.type) {
    case 'artifact.started':
    case 'artifact.chunk.started': {
      const d = (ev.data || {}) as { item_id?: string; itemId?: string };
      const rawId = typeof (d.item_id || d.itemId) === 'string' ? (d.item_id || d.itemId)! : ev.id;
      if (!rawId) return;
      dispatch(startRun({ convId, stableId: stableIdFromItemId(rawId), now }));
      return;
    }
    case 'artifact.delta': {
      const d = (ev.data || {}) as { item_id?: string; delta?: string };
      const rawId = typeof d.item_id === 'string' ? d.item_id : ev.id;
      if (!rawId) return;
      const delta = typeof d.delta === 'string' ? d.delta : '';
      dispatch(appendRunDelta({ convId, stableId: stableIdFromItemId(rawId), delta, now }));
      return;
    }
    case 'artifact.chunk.completed': {
      const d = (ev.data || {}) as { item_id?: string; content?: string };
      const rawId = typeof d.item_id === 'string' ? d.item_id : ev.id;
      if (!rawId) return;
      const content = typeof d.content === 'string' ? d.content : '';
      dispatch(
        appendRunDelta({ convId, stableId: stableIdFromItemId(rawId), delta: content, now })
      );
      return;
    }
    case 'artifact.completed': {
      const d = (ev.data || {}) as { item_id?: string; success?: boolean; error?: string };
      const rawId = typeof d.item_id === 'string' ? d.item_id : ev.id;
      if (!rawId) return;
      const success = typeof d.success === 'boolean' ? d.success : true;
      const error = typeof d.error === 'string' ? d.error : undefined;
      dispatch(completeRun({ convId, stableId: stableIdFromItemId(rawId), success, error, now }));
      return;
    }
    case 'artifact.persisted': {
      const d = (ev.data || {}) as {
        item_id?: string;
        artifact_id?: string;
        revision?: number;
        title?: string;
      };
      const rawItemId = typeof d.item_id === 'string' ? d.item_id : ev.id;
      const artifactId = typeof d.artifact_id === 'string' ? d.artifact_id : '';
      if (!rawItemId || !artifactId) return;
      const revision = typeof d.revision === 'number' ? d.revision : null;
      const title = typeof d.title === 'string' ? d.title : undefined;
      dispatch(
        persistRun({
          convId,
          stableId: stableIdFromItemId(rawItemId),
          artifactId,
          revision,
          title,
          now,
        })
      );
      return;
    }
    default:
      return;
  }
}

/**
 * Handle SEM events that aren't routed by the registry
 * This is the fallback handler for event types not covered by handleSem()
 */
export function handleSemEventFallback(params: {
  dispatch: Dispatch;
  convId: string;
  ev: SemEvent;
  now: number;
}): void {
  const { dispatch, convId, ev, now } = params;

  switch (ev.type) {
    case 'artifact.delta': {
      const d = (ev.data || {}) as { item_id?: string; delta?: string };
      const rawId = typeof d.item_id === 'string' ? d.item_id : ev.id;
      const delta = typeof d.delta === 'string' ? d.delta : '';

      const entityId = stableIdFromItemId(rawId);
      const namespacedId = `editable_summary:${entityId}`;

      if (namespacedId && delta) {
        dispatch(
          addEntity({
            convId,
            entity: {
              id: namespacedId,
              kind: 'editable_summary',
              timestamp: now,
              props: { status: 'active', content: '', chunks: [] },
            } as any,
          })
        );

        dispatch(
          appendEntityArrayItem({
            convId,
            entityId: namespacedId,
            key: 'chunks',
            item: delta,
          })
        );
      }
      break;
    }
    case 'artifact.completed': {
      const d = (ev.data || {}) as { item_id?: string; success?: boolean; error?: string };
      const rawId = typeof d.item_id === 'string' ? d.item_id : ev.id;
      const success = typeof d.success === 'boolean' ? d.success : true;
      const errorText = typeof d.error === 'string' ? d.error : '';

      const entityId = stableIdFromItemId(rawId);
      const namespacedId = `editable_summary:${entityId}`;

      dispatch(
        addEntity({
          convId,
          entity: {
            id: namespacedId,
            kind: 'editable_summary',
            timestamp: now,
            props: { status: 'active', content: '', chunks: [] },
          } as any,
        })
      );

      dispatch(
        updateEntity({
          convId,
          entityId: namespacedId,
          props: {
            status: success ? 'completed' : 'error',
            success,
            ...(errorText ? { error: errorText } : {}),
          },
        })
      );
      break;
    }
    case 'artifact.chunk.completed': {
      const d = (ev.data || {}) as { item_id?: string; content?: string };
      const rawId = typeof d.item_id === 'string' ? d.item_id : ev.id;
      const chunk = typeof d.content === 'string' ? d.content : '';

      const entityId = stableIdFromItemId(rawId);
      const namespacedId = `editable_summary:${entityId}`;

      if (namespacedId && chunk) {
        dispatch(
          addEntity({
            convId,
            entity: {
              id: namespacedId,
              kind: 'editable_summary',
              timestamp: now,
              props: { status: 'active', content: '', chunks: [] },
            } as any,
          })
        );

        dispatch(
          appendEntityArrayItem({
            convId,
            entityId: namespacedId,
            key: 'chunks',
            item: chunk,
          })
        );
      }
      break;
    }
    case 'artifact.persisted': {
      const d = (ev.data || {}) as {
        item_id?: string;
        artifact_id?: string;
        revision?: number;
        title?: string;
      };
      const artifactId = typeof d.artifact_id === 'string' ? d.artifact_id : null;
      const rawItemId = typeof d.item_id === 'string' ? d.item_id : ev.id;
      const revision = typeof d.revision === 'number' ? d.revision : null;
      const title = typeof d.title === 'string' ? d.title.trim() : '';

      if (!artifactId || !convId) {
        break;
      }

      const stableId = stableIdFromItemId(rawItemId);
      const summaryEntityId = `editable_summary:${stableId}`;

      dispatch(
        addEntity({
          convId,
          entity: {
            id: summaryEntityId,
            kind: 'editable_summary',
            timestamp: now,
            props: { status: 'active', content: '', chunks: [] },
          } as any,
        })
      );

      dispatch(
        updateEntity({
          convId,
          entityId: summaryEntityId,
          props: {
            artifact_id: artifactId,
            ...(revision && revision > 0 ? { artifact_revision: revision } : {}),
            ...(title ? { title } : {}),
          },
        })
      );

      if (typeof revision === 'number' && revision > 0) {
        const metaEntity: ArtifactMetaEntity = {
          id: `artifact_meta:${artifactId}@${revision}`,
          kind: 'artifact_meta',
          timestamp: now,
          props: {
            artifact_id: artifactId,
            revision,
            ...(title ? { title } : {}),
          },
        };
        dispatch(upsertEntity({ convId, entity: metaEntity as any }));
      }
      break;
    }
    case 'team.analysis.start': {
      const startData = (ev.data || {}) as {
        analysis_id?: string;
        team_size?: number;
        depth?: string;
      };
      const entityId = typeof startData.analysis_id === 'string' ? startData.analysis_id : ev.id;
      const ent: TeamAnalysisEntity = {
        id: entityId,
        kind: 'team_analysis',
        timestamp: now,
        props: {
          status: 'analyzing',
          teamSize: typeof startData.team_size === 'number' ? startData.team_size : undefined,
          depth: typeof startData.depth === 'string' ? startData.depth : undefined,
          progress: 0,
        },
      };
      dispatch(addEntity({ convId, entity: ent }));
      break;
    }
    case 'team.analysis.progress': {
      if (ev.id && ev.data) {
        const progData = ev.data as {
          analysis_id?: string;
          progress?: number;
          analyzing?: string;
        };
        const entityId = typeof progData.analysis_id === 'string' ? progData.analysis_id : ev.id;
        const progressVal = typeof progData.progress === 'number' ? progData.progress : 0;
        const currentPairVal =
          typeof progData.analyzing === 'string' ? progData.analyzing : undefined;
        dispatch(
          upsertEntity({
            convId,
            entity: {
              id: entityId,
              kind: 'team_analysis',
              timestamp: now,
              props: {
                status: 'analyzing',
                progress: progressVal,
                currentPair: currentPairVal,
              },
            } as TeamAnalysisEntity,
          })
        );
      }
      break;
    }
    case 'team.analysis.result': {
      if (ev.id && ev.data) {
        const resData = ev.data as {
          analysis_id?: string;
          network_score?: number;
          insights?: string[];
          visualization_data?: {
            nodes: Array<{ id: string; label: string }>;
            edges: Array<{
              source: string;
              target: string;
              strength: number;
              sentiment: string;
            }>;
          };
        };
        const entityId = typeof resData.analysis_id === 'string' ? resData.analysis_id : ev.id;
        dispatch(
          upsertEntity({
            convId,
            entity: {
              id: entityId,
              kind: 'team_analysis',
              timestamp: now,
              props: {
                status: 'completed',
                progress: 1,
                networkScore:
                  typeof resData.network_score === 'number' ? resData.network_score : undefined,
                insights: Array.isArray(resData.insights) ? resData.insights : undefined,
                visualizationData: resData.visualization_data,
              },
            } as TeamAnalysisEntity,
          })
        );
      }
      break;
    }
    case 'planning.start': {
      const data = (ev.data || {}) as {
        provider?: string;
        planner_model?: string;
      };
      const msg = `Planning with ${data.provider || 'agent'} (${data.planner_model || 'model'})`;
      const ent: StatusEntity = {
        id: ev.id,
        kind: 'status',
        timestamp: now,
        props: { text: msg, type: 'info' },
      };
      dispatch(addEntity({ convId, entity: ent }));
      break;
    }
    case 'planning.iteration': {
      const data = (ev.data || {}) as {
        iteration?: number;
        decision?: string;
        strategy?: string;
        progress?: string;
        tool_name?: string;
        reflection_text?: string;
      };

      let msg = `Iteration ${data.iteration || '?'}: ${data.decision || 'deciding'}`;
      if (data.tool_name) {
        msg += ` [${data.tool_name}]`;
      }
      const detail = data.progress || data.strategy;
      if (detail) {
        msg += ` - ${detail}`;
      }
      if (data.reflection_text) {
        msg += `\nReflection: ${data.reflection_text}`;
      }

      const ent: StatusEntity = {
        id: ev.id,
        kind: 'status',
        timestamp: now,
        props: { text: msg, type: 'info' },
      };
      dispatch(upsertEntity({ convId, entity: ent }));
      break;
    }
    case 'planning.reflection': {
      const data = (ev.data || {}) as { reflection_text?: string; iteration?: number };
      const iterInfo = data.iteration ? ` (iteration ${data.iteration})` : '';
      const msg = `Reflection${iterInfo}: ${data.reflection_text || 'Reviewing progress...'}`;
      const ent: StatusEntity = {
        id: ev.id,
        kind: 'status',
        timestamp: now,
        props: { text: msg, type: 'info' },
      };
      dispatch(upsertEntity({ convId, entity: ent }));
      break;
    }
    case 'planning.complete': {
      const data = (ev.data || {}) as {
        total_iterations?: number;
        final_decision?: string;
        reason?: string;
      };
      const msg = `Planning complete after ${data.total_iterations || '?'} iterations (${data.reason || data.final_decision || 'done'})`;
      const ent: StatusEntity = {
        id: ev.id,
        kind: 'status',
        timestamp: now,
        props: { text: msg, type: 'success' },
      };
      dispatch(upsertEntity({ convId, entity: ent }));
      break;
    }
    case 'execution.start': {
      const data = (ev.data || {}) as { executor_model?: string };
      const msg = `Generating response with ${data.executor_model || 'executor'}...`;
      const ent: StatusEntity = {
        id: ev.id,
        kind: 'status',
        timestamp: now,
        props: { text: msg, type: 'info' },
      };
      dispatch(addEntity({ convId, entity: ent }));
      break;
    }
    case 'execution.complete': {
      const msg = 'Response generation complete';
      const ent: StatusEntity = {
        id: ev.id,
        kind: 'status',
        timestamp: now,
        props: { text: msg, type: 'success' },
      };
      dispatch(upsertEntity({ convId, entity: ent }));
      break;
    }
    case 'llm.start': {
      clearLlmArtifactProjectionState(convId, ev.id);
      const ent: MessageEntity = {
        id: ev.id,
        kind: 'message',
        timestamp: now,
        props: {
          role: 'assistant',
          content: '',
          streaming: true,
          metadata: ev.metadata || {},
        },
      };
      dispatch(addEntity({ convId, entity: ent }));
      break;
    }
    case 'llm.delta': {
      if (ev.id) {
        const state = getOrCreateLlmArtifactProjectionState(convId, ev.id);
        const hasCumulative = typeof ev.cumulative === 'string';
        const fullText = hasCumulative
          ? ev.cumulative!
          : `${state.lastCumulative}${typeof ev.delta === 'string' ? ev.delta : ''}`;
        const maybeArtifactStream =
          state.summaryStarted ||
          state.insideArtifact ||
          fullText.includes(ARTIFACT_OPEN_TAG) ||
          fullText.includes(ARTIFACT_CLOSE_TAG);

        if (!maybeArtifactStream) {
          const visibleOutside = stripTrailingArtifactTagPrefix(fullText, false);
          if (visibleOutside.length > state.outsideLength) {
            const outsideDelta = visibleOutside.slice(state.outsideLength);
            dispatch(
              appendMessageText({
                convId,
                messageId: ev.id,
                text: outsideDelta,
              })
            );
          }
          state.lastCumulative = fullText;
          state.outsideLength = visibleOutside.length;
          state.artifactLength = 0;
          state.insideArtifact = false;
          break;
        }

        projectLlmArtifactTaggedText({
          dispatch,
          convId,
          messageID: ev.id,
          now,
          fullText,
          shouldFinalizeMessage: false,
        });
      }
      break;
    }
    case 'llm.final': {
      if (ev.id) {
        const state = getOrCreateLlmArtifactProjectionState(convId, ev.id);
        const fullText = typeof ev.text === 'string' ? ev.text : state.lastCumulative;
        const maybeArtifactStream =
          state.summaryStarted ||
          state.insideArtifact ||
          fullText.includes(ARTIFACT_OPEN_TAG) ||
          fullText.includes(ARTIFACT_CLOSE_TAG);

        if (!maybeArtifactStream) {
          dispatch(
            finalizeMessage({
              convId,
              messageId: ev.id,
              fullText: fullText || undefined,
            })
          );
          clearLlmArtifactProjectionState(convId, ev.id);
          break;
        }

        projectLlmArtifactTaggedText({
          dispatch,
          convId,
          messageID: ev.id,
          now,
          fullText,
          shouldFinalizeMessage: true,
        });
        clearLlmArtifactProjectionState(convId, ev.id);
      }
      break;
    }
    case 'tool.start': {
      const ent: ToolCallEntity = {
        id: ev.id,
        kind: 'tool_call',
        timestamp: now,
        props: {
          name: ev.name || 'tool',
          input: ev.input,
          status: 'running',
          progress: 0,
        },
      };
      dispatch(addEntity({ convId, entity: ent }));
      break;
    }
    case 'tool.delta': {
      const progress = typeof ev.patch?.progress === 'number' ? ev.patch.progress : undefined;
      dispatch(
        updateToolProgress({
          convId,
          toolId: ev.id,
          progress: progress ?? 0,
          status: 'running',
        })
      );
      break;
    }
    case 'tool.result': {
      try {
        const toolName = (ev as any).tool_name || (ev as any).name || 'tool';
        const provider = toolLabelProviderFor(String(toolName));
        const fromProvider = provider.labelForResult(String(toolName), ev.result);
        if (
          fromProvider &&
          (typeof fromProvider.resultCount === 'number' || fromProvider.resultSummary)
        ) {
          dispatch(
            upsertEntity({
              convId,
              entity: {
                id: ev.id,
                kind: 'tool_call',
                timestamp: now,
                props: {
                  status: 'completed',
                  progress: 1,
                  resultSummary: fromProvider.resultSummary,
                  resultCount: fromProvider.resultCount,
                },
              } as ToolCallEntity,
            })
          );
        }
      } catch {
        // ignore summary errors
      }

      const res: ToolResultEntity = {
        id: `${ev.id}-result-${now}`,
        kind: 'tool_result',
        timestamp: now,
        props: {
          toolCallId: ev.id,
          result: ev.result,
        },
      };
      dispatch(addEntities({ convId, entities: [res] }));
      dispatch(
        updateToolProgress({
          convId,
          toolId: ev.id,
          progress: 100,
          status: 'completed',
        })
      );
      break;
    }
    case 'tool.done': {
      dispatch(
        updateToolProgress({
          convId,
          toolId: ev.id,
          progress: 100,
          status: 'completed',
        })
      );
      break;
    }
    case 'agent.mode': {
      const ent: StatusEntity = {
        id: ev.id,
        kind: 'status',
        timestamp: now,
        props: {
          text: ev.title || 'Agent mode',
          type: 'info',
          ...(ev.data || {}),
        },
      };
      dispatch(addEntity({ convId, entity: ent }));
      break;
    }
    case 'log': {
      const { message, type: logType, ...additionalFields } = ev as any;
      const ent: StatusEntity = {
        id: ev.id,
        kind: 'status',
        timestamp: now,
        props: {
          text: message || 'log',
          type: (logType as any) || 'info',
          ...additionalFields,
        },
      };
      dispatch(addEntity({ convId, entity: ent }));
      break;
    }
    default:
      // ignore unknown types
      break;
  }
}

/**
 * Main entry point for routing SEM events
 * Tries registry handlers first, then falls back to handleSemEventFallback
 */
export function routeSemEvent(params: {
  dispatch: Dispatch;
  convId: string;
  ev: SemEvent;
  now: number;
}): void {
  const { dispatch, convId, ev, now } = params;

  // Handle summary UI events first
  dispatchSummaryUiSemEvent({ dispatch, convId, ev, now });

  // Try registry-based handlers
  const routed = handleSem({ ev: ev as any, now, convId });
  if (routed) {
    if (routed.kind === 'add') {
      dispatch(addEntity(routed));
    } else {
      dispatch(upsertEntity(routed));
    }
    return;
  }

  // Fallback to switch statement for unhandled types
  handleSemEventFallback({ dispatch, convId, ev, now });
}
